/**
 * Packet API Service Layer (V2)
 * This module provides functions for interacting with the new scalable packet generation system.
 * All functions use the new REST API endpoints under /api/packet/*
 */

import * as Sentry from "@sentry/react";
import axiosInstance from "./axiosConfig";
import { APIError, handleAPIError } from "./utils/errors";
import { FilingMaterialsExport } from "./utils/filingMaterialsExport";
import {
  Packet,
  PacketSettingsPatch,
  GeneratePacketRequest,
  GeneratePacketAsyncResponse
} from "./types/packet";

const BASE_URL = "/packet";

/**
 * Get the generated PDF for a packet (RBAC-protected).
 * @param packetId - The packet ID whose PDF should be returned
 * @returns Promise<Blob> - The PDF as a blob
 * @throws {APIError} If retrieval fails
 */
export const getPacketPdf = async (packetId: string): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(`${BASE_URL}/${packetId}/pdf`, {
      responseType: "blob",
      headers: {
        Accept: "application/pdf"
      }
    });
    const responseData = response.data;
    if (responseData instanceof Blob) {
      if (responseData.type === "application/pdf") {
        return responseData;
      }
      return responseData.slice(0, responseData.size, "application/pdf");
    }
    return new Blob([responseData], { type: "application/pdf" });
  } catch (error) {
    Sentry.captureException(error);
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

/**
 * The packet PDF as a user downloads it (`?download=1`): its divider titles are
 * page text other editors (Acrobat's Edit PDF) can change, and the download is
 * logged as activity.
 * @param packetId - The packet ID whose PDF should be downloaded
 * @returns Promise<Blob> - The PDF as a blob
 * @throws {APIError} If retrieval fails
 */
export const downloadPacketPdf = async (packetId: string): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(`${BASE_URL}/${packetId}/pdf`, {
      params: { download: 1 },
      responseType: "blob",
      headers: {
        Accept: "application/pdf"
      }
    });
    const responseData = response.data;
    if (responseData instanceof Blob) {
      return responseData.type === "application/pdf"
        ? responseData
        : responseData.slice(0, responseData.size, "application/pdf");
    }
    return new Blob([responseData], { type: "application/pdf" });
  } catch (error) {
    Sentry.captureException(error);
    throw handleAPIError(error);
  }
};

/**
 * Get a packet by its ID
 * @param packetId - The unique identifier of the packet
 * @returns Promise<Packet> - The requested packet
 * @throws {APIError} If the packet is not found or request fails
 *
 * @example
 * ```typescript
 * const packet = await getPacketById("pkt_123");
 * console.log(packet.pdf_url); // Download URL
 * ```
 */
export const getPacketById = async (packetId: string): Promise<Packet> => {
  try {
    const response = await axiosInstance.get<Packet>(`${BASE_URL}/${packetId}`);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

/**
 * Generate an existing packet by packet_id (enqueues async generation)
 * This marks the packet as GENERATING and enqueues a background task.
 *
 * @param packetId - The packet ID to generate
 * @param options - Optional generation configuration
 * @returns Promise<GeneratePacketAsyncResponse> - Success message and tracking IDs
 * @throws {APIError} If generation fails
 *
 * @example
 * ```typescript
 * const result = await generatePacket("pkt_123", {
 *   packet_name: "EB1A Petition",
 *   divider: {
 *     enabled: true,
 *     mainGroupEnabled: true,
 *     categoryEnabled: true,
 *     documentEnabled: false,
 *     color: "#4A90E2"
 *   }
 * });
 * ```
 */
export const generatePacket = async (
  packetId: string,
  options?: Omit<GeneratePacketRequest, "individual_id">
): Promise<GeneratePacketAsyncResponse> => {
  try {
    const response = await axiosInstance.post<GeneratePacketAsyncResponse>(
      `${BASE_URL}/generate`,
      {
        packet_id: packetId,
        ...options
      }
    );
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

/**
 * Get or create a packet for an individual
 * This endpoint retrieves the existing packet for an individual or creates one if it doesn't exist.
 *
 * @param individualId - The individual's ID
 * @returns Promise<Packet> - The packet for this individual
 * @throws {APIError} If retrieval fails
 *
 * @example
 * ```typescript
 * const packet = await getPacketByIndividual("ind_123");
 * console.log(packet.packet_id); // The packet ID
 * console.log(packet.status); // Current status (NOT_GENERATED, GENERATING, etc.)
 * ```
 */
export const getPacketByIndividual = async (
  individualId: string
): Promise<Packet> => {
  try {
    const response = await axiosInstance.get<Packet>(
      `${BASE_URL}/by-individual/${individualId}`
    );
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

/**
 * Read an async filing-materials export the agent started.
 * `status` leaves "processing" once the ZIP is on GCS and `download_url` is set.
 */
export const getFilingMaterialsExport = async (
  individualId: string,
  exportId: string
): Promise<FilingMaterialsExport> => {
  try {
    const response = await axiosInstance.get<{ export: FilingMaterialsExport }>(
      `${BASE_URL}/by-individual/${individualId}/filing-materials-export/${exportId}`
    );
    return response.data.export;
  } catch (error) {
    Sentry.captureException(error);
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

/**
 * Merge build options (name, tabs, page numbers) into the packet's saved
 * settings without rebuilding; the next build uses them.
 */
export const updatePacketSettings = async (
  packetId: string,
  patch: PacketSettingsPatch
): Promise<Packet> => {
  try {
    const response = await axiosInstance.patch<Packet>(
      `${BASE_URL}/${packetId}/settings`,
      patch
    );
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

/** One row of what a packet editor save changes, for the confirmation. */
export type PacketEditorChange = {
  kind: "file" | "tab" | "move" | "reorder" | "trash";
  node_id: string | null;
  label: string | null;
  title: string;
  details: string[];
  /** A tab whose title was retyped; `override` null resets it to the file name. */
  title_edit?: { previous: string; override: string | null };
};

export type PacketEditorSaveResult = {
  packet_id: string;
  dry_run: boolean;
  changes: PacketEditorChange[];
  /** The save moves an exhibit to the trash; ask before committing. */
  needs_confirmation: boolean;
  generate_call_id?: string;
  rebuild_error?: string;
};

export type PacketEditorSaveRequest = {
  pdf: Blob;
  /** One per page of `pdf`: the built packet page it is, or an insert. */
  provenance: Array<
    { src: "packet"; page: number } | { src: "insert"; label?: string }
  >;
  /** Pending redactions by 1-based page of `pdf`, top-left origin. */
  redactions?: Array<{
    page: number;
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  }>;
  /** `{ "<1-based page of pdf>": degrees }`. */
  rotations?: Record<number, number>;
  /** The manifest `content_hash` the editor loaded. */
  baseHash?: string | null;
  dryRun?: boolean;
  allowTrash?: boolean;
};

/**
 * Save the PDF editor's output on the packet: marks, redactions, turns and
 * page moves land on the exhibit files, the tab layer and the case tree, then
 * the packet is rebuilt. `dryRun` returns the summary and writes nothing.
 */
export const saveEditorPacket = async (
  packetId: string,
  request: PacketEditorSaveRequest
): Promise<PacketEditorSaveResult> => {
  const form = new FormData();
  form.append("file", request.pdf, "packet.pdf");
  form.append("provenance", JSON.stringify(request.provenance));
  if (request.redactions?.length) {
    form.append("redactions", JSON.stringify(request.redactions));
  }
  const rotations = Object.fromEntries(
    Object.entries(request.rotations ?? {}).filter(
      ([, degrees]) => degrees % 360 !== 0
    )
  );
  if (Object.keys(rotations).length) {
    form.append("rotations", JSON.stringify(rotations));
  }
  if (request.baseHash) form.append("base_hash", request.baseHash);
  if (request.dryRun) form.append("dry_run", "true");
  if (request.allowTrash) form.append("allow_trash", "true");
  try {
    const response = await axiosInstance.post<PacketEditorSaveResult>(
      `${BASE_URL}/${packetId}/editor-save`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  } catch (error) {
    const apiError = handleAPIError(error);
    if (apiError.status !== 400 && apiError.status !== 409) {
      Sentry.captureException(error);
    }
    throw apiError;
  }
};

// Export as a namespace object for consistency with existing API patterns
export const PacketsV2API = {
  getById: getPacketById,
  getByIndividual: getPacketByIndividual,
  generate: generatePacket,
  updateSettings: updatePacketSettings,
  getPdf: getPacketPdf,
  downloadPdf: downloadPacketPdf,
  getFilingMaterialsExport,
  saveEditorPacket
};
